<template>
  <PortalForm
    :portalType="portalType"
    :title="title"
    :headerIconSrc="policyChangesIcon"
    :steps="steps"
    :current-step="currentStep"
    :form-data="formData"
    :form-valid="isStepValid"
    :loading="loading"
    :success-title="successTitle"
    :success-message="successMessage"
    :reference-number="referenceNumber"
    :success-details="successDetails"
    :next-steps="nextSteps"
    :enable-email-receipt="true"
    :user-email="formData.email"
    :form-id="'auto-policy-change'"
    @update:formData="updateFormData"
    @step-change="handleStepChange"
    @form-submit="submitForm"
    @draft-restored="handleDraftRestored"
    @back="navigateAway"
    @primary-action="returnToDashboard"
    @email-sent="handleEmailSent"
  >
    <!-- Contact Information Step -->
    <SharedContactInformationStep 
      v-if="currentStep === 1"
      v-model:policyNumber="formData.policyNumber"
      v-model:email="formData.email"
      :customerType="portalType"
      :errors="errors"
      @next="goToNextStep"
    >
      <template #additional-fields>
        <div class="form-field">
          <label
            for="policyholder-name"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Policyholder Name</label>
          <input
            :value="formData.policyholderName"
            @input="formData.policyholderName = $event.target.value"
            type="text"
            id="policyholder-name"
            class="capitalize input-responsive"
            placeholder="Full name as it appears on policy"
          />
          <p v-if="errors.policyholderName" class="text-red-500 text-xs italic">
            {{ errors.policyholderName }}
          </p>
        </div>
      </template>
    </SharedContactInformationStep>
    
    <!-- Policy Changes Step -->
    <PolicyChangesStep
      v-if="currentStep === 2"
      v-model:activeChanges="formData.activeChanges"
      :customerType="portalType"
      :errors="errors"
      @previous="goToPreviousStep"
      @next="goToNextStep"
      ref="policyChangesRef"
    />
    
    <!-- Document Upload Step (New) -->
    <SharedDocumentUploadStep
      v-if="currentStep === 3 && props.portalType !== 'lender'"
      v-model:documents="formData.documents"
      :customerType="portalType"
      :errors="errors"
      :document-types="documentTypes"
      @previous="goToPreviousStep"
      @next="goToNextStep"
    />
    
    <!-- Review and Submit Step -->
    <SharedEffectiveDateStep
      v-if="(currentStep === 4 && props.portalType !== 'lender') || (currentStep === 3 && props.portalType === 'lender')"
      v-model:effectiveDate="formData.effectiveDate"
      v-model:comments="formData.comments"
      :customerType="portalType"
      :isSubmitting="isSubmitting"
      :errors="errors"
      @previous="goToPreviousStep"
      @submit="handleSubmit"
    >
      <template #additional-fields>
        <div class="form-field">
          <label
            for="signature"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Electronic Signature</label>
          <input
            :value="formData.signature"
            @input="formData.signature = $event.target.value"
            type="text"
            id="signature"
            class="capitalize input-responsive"
            placeholder="Type your full name to sign"
          />
          <p v-if="errors.signature" class="text-red-500 text-xs italic">
            {{ errors.signature }}
          </p>
        </div>
        
        <div class="form-field">
          <label
            for="signature-date"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Signature Date</label>
          <input
            type="date"
            id="signature-date"
            :value="formData.signatureDate"
            @input="formData.signatureDate = $event.target.value"
            class="input-responsive"
            :max="today"
          />
          <p v-if="errors.signatureDate" class="text-red-500 text-xs italic">
            {{ errors.signatureDate }}
          </p>
        </div>
        
        <!-- Authorization file upload for lenders -->
        <div v-if="portalType === 'lender'" class="form-field col-span-1 md:col-span-2">
          <label
            for="authorization-file"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Borrower Authorization</label>
          <div class="flex items-center">
            <label 
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-l-md border border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
            >
              Choose File
              <input
                type="file"
                id="authorization-file"
                class="hidden"
                @change="handleFileUpload"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </label>
            <span class="ml-3 text-gray-600 text-sm">
              {{ formData.authorizationFile ? formData.authorizationFile.name : 'No file chosen' }}
            </span>
          </div>
          <p class="text-gray-500 text-xs mt-1">
            Please upload a signed authorization from the borrower (PDF, Word, or image).
          </p>
          <p v-if="errors.authorizationFile" class="text-red-500 text-xs italic">
            {{ errors.authorizationFile }}
          </p>
        </div>
      </template>
    </SharedEffectiveDateStep>
  </PortalForm>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import PortalForm from '@/components/common/PortalForm.vue';
import SharedContactInformationStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedContactInformationStep.vue';
import PolicyChangesStep from './steps/PolicyChangesStep.vue';
import SharedEffectiveDateStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedEffectiveDateStep.vue';
import SharedDocumentUploadStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedDocumentUploadStep.vue';
import { useAutoInsuranceService } from '@/services/autoInsuranceService';
import { validateEmail, validatePolicyNumber } from '@/utils/validators';
import { validateEffectiveDate } from '@/utils/policyValidators';
import { useRouter } from 'vue-router';
import policyChangesIcon from '@/assets/images/ui/icons/policyChanges.svg';
import { useErrorReportingService } from '@/services/errorReportingService';
import { getTodayFormatted } from '@/utils/dateUtils';

// Add these at the top of the script after imports but before the props definition
const MIN_VALIDATION_INTERVAL = 800; // 800ms minimum between validations
const validationTimestamps = {
  step1: 0,
  step2: 0,
  step3: 0,
  step4: 0
};

// Props
const props = defineProps({
  title: {
    type: String,
    default: 'Auto Policy Change Request'
  },
  portalType: {
    type: String,
    default: 'customer',
    validator: value => ['customer', 'lender'].includes(value)
  }
});

// Router for navigation
const router = useRouter();
const autoInsuranceService = useAutoInsuranceService();
const { logFormError } = useErrorReportingService();

// Form state
const currentStep = ref(1);
const loading = ref(false);
const isSubmitting = ref(false);
const errors = ref({});
const policyChangesRef = ref(null);
const referenceNumber = ref('');

// Reactive validation states
const isStep1Validated = ref(true);
const isStep2Validated = ref(true);
const isStep3Validated = ref(true);
const isStep4Validated = ref(true);

// Success state
const successTitle = ref('Request Submitted Successfully!');
const successMessage = ref('Your auto policy change request has been received and is being processed.');
const successDetails = ref([]);
const nextSteps = ref([
  'A confirmation email will be sent to you shortly.',
  'An agent will review your request within 1-2 business days.',
  'If additional information is needed, we will contact you via email or phone.'
]);

// Fix timezone issues with date by ensuring we get today's date in local timezone
const today = computed(() => getTodayFormatted());

// Form data - using a single reactive object for easier state management
const formData = ref({
  policyNumber: '',
  policyholderName: '',
  email: '',
  activeChanges: [],
  documents: [],
  effectiveDate: today.value,
  signature: '',
  signatureDate: today.value,
  authorizationFile: null,
  comments: ''
});

// Document types required for this form
const documentTypes = [
  { id: 'vehicle_photo', label: 'Vehicle Photos', description: 'Clear photos of your vehicle from all angles', required: false },
  { id: 'drivers_license', label: 'Driver\'s License', description: 'Current driver\'s license for all drivers', required: true },
  { id: 'vehicle_registration', label: 'Vehicle Registration', description: 'Current registration for the vehicle', required: true },
  { id: 'proof_of_insurance', label: 'Proof of Prior Insurance', description: 'Documentation of your current or prior insurance', required: false }
];

// Computed properties
const steps = computed(() => {
  const baseSteps = [
    { label: 'Policy Information', index: 1 },
    { label: 'Request Changes', index: 2 },
    // Step 3 (Documents) is conditional
    { label: 'Review and Submit', index: 4 } // Note: index remains 4 for mapping to validation logic
  ];

  if (props.portalType === 'lender') {
    // Remove document upload step for lenders, re-index remaining steps for display
    return [
      { label: 'Policy Information', index: 1 },
      { label: 'Request Changes', index: 2 },
      { label: 'Review and Submit', index: 3 } // Display index is 3, but maps to original step 4 logic
    ];
  } else {
    // For customers, include the document upload step
    return [
      { label: 'Policy Information', index: 1 },
      { label: 'Request Changes', index: 2 },
      { label: 'Documents', index: 3 },
      { label: 'Review and Submit', index: 4 }
    ];
  }
});

// Check if current step is valid
const isStepValid = computed(() => {
  let validStatus = true; // Default to true, changed from "let validStatus;"
  
  if (props.portalType === 'lender') {
    // Lender flow: Step 1 (Policy Info), Step 2 (Request Changes), Step 3 (Review/Submit)
    if (currentStep.value === 1) validStatus = !!isStep1Validated.value;
    else if (currentStep.value === 2) validStatus = !!isStep2Validated.value;
    else if (currentStep.value === 3) validStatus = !!isStep4Validated.value; // Lender's 3rd step uses original step 4's validation state
    else validStatus = true; // Default for unhandled steps in this flow
  } else {
    // Customer flow: Step 1, Step 2, Step 3 (Documents), Step 4 (Review/Submit)
    if (currentStep.value === 1) validStatus = !!isStep1Validated.value;
    else if (currentStep.value === 2) validStatus = !!isStep2Validated.value;
    else if (currentStep.value === 3) validStatus = !!isStep3Validated.value;
    else if (currentStep.value === 4) validStatus = !!isStep4Validated.value;
    else validStatus = true; // Default for unhandled steps in this flow
  }
  
  // Ensure we always return a boolean
  if (typeof validStatus !== 'boolean') {
    console.warn(`[AutoPolicyChangeForm] Non-boolean validation state detected: ${typeof validStatus}. Converting to boolean.`);
    validStatus = !!validStatus;
  }
  
  console.log(`[AutoPolicyChangeForm] isStepValid computed for currentStep: ${currentStep.value}, portalType: ${props.portalType}. Result: ${validStatus}`);
  return validStatus;
});

// File handling
const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    formData.value.authorizationFile = file;
    errors.value.authorizationFile = '';
  }
};

// Validation methods - returns validation result but only sets errors if updateErrors is true
const validateStep1 = (updateErrors = true) => {
  console.log(`[AutoPolicyChangeForm] validateStep1 called. updateErrors: ${updateErrors}`);
  console.log(`[AutoPolicyChangeForm] formData for Step 1 validation:`, JSON.parse(JSON.stringify({
    policyNumber: formData.value.policyNumber,
    policyholderName: formData.value.policyholderName,
    email: formData.value.email
  })));

  const tempErrors = {
    policyNumber: validatePolicyNumber(formData.value.policyNumber),
    policyholderName: !formData.value.policyholderName ? 'Policyholder name is required' : '',
    email: validateEmail(formData.value.email)
  };
  
  const isValid = !Object.values(tempErrors).some(error => error);
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  isStep1Validated.value = isValid;
  console.log(`[AutoPolicyChangeForm] validateStep1 finished. isValid: ${isValid}, isStep1Validated set to: ${isStep1Validated.value}`);
  return isValid;
};

const validateStep2 = (updateErrors = true) => {
  // Anti-loop protection: prevent too frequent validations
  const now = Date.now();
  if (now - validationTimestamps.step2 < MIN_VALIDATION_INTERVAL) {
    console.log(`[AutoPolicyChangeForm] Throttling step 2 validation (${now - validationTimestamps.step2}ms since last check)`);
    return isStep2Validated.value; // Return last known state
  }
  validationTimestamps.step2 = now;
  
  // Check if at least one change is selected
  let isChangeValid = true;
  let tempErrors = {};
  
  if (!formData.value.activeChanges || !Array.isArray(formData.value.activeChanges) || formData.value.activeChanges.length === 0) {
    tempErrors.changes = 'Please add at least one change request';
    isChangeValid = false;
  } else {
    tempErrors.changes = '';
    
    // Synchronously validate using component's validate method
    if (policyChangesRef.value && typeof policyChangesRef.value.validate === 'function') {
      try {
        // Get the validation result and ensure it's a boolean
        const validationResult = policyChangesRef.value.validate();
        
        // Always treat as a synchronous result to prevent reactivity issues
        if (typeof validationResult === 'boolean') {
          isChangeValid = validationResult;
        } else if (validationResult instanceof Promise) {
          console.warn('[AutoPolicyChangeForm] Async validation detected, using current state instead');
          // Don't use Promise results - they cause reactivity issues
        } else {
          console.warn(`[AutoPolicyChangeForm] Unexpected validation result type: ${typeof validationResult}`);
        }
      } catch (err) {
        console.error('[AutoPolicyChangeForm] Validation error in step 2:', err);
        isChangeValid = false;
      }
    }
  }
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  
  // Ensure we set a boolean value, not a Promise
  isStep2Validated.value = !!isChangeValid;
  return isStep2Validated.value;
};

const validateStep3 = (updateErrors = true) => {
  // If customer, document upload is optional, bypass 'required' flags in documentTypes
  if (props.portalType === 'customer') {
    isStep3Validated.value = true;
    if (updateErrors) errors.value.documents = ''; // Clear any previous document errors
    return true;
  }

  // For Lenders: proceed with existing validation based on documentTypes 'required' flags
  if (!documentTypes || !Array.isArray(documentTypes)) {
    isStep3Validated.value = true; // If no document types are defined, assume validation passes
    return true; 
  }
  
  const requiredDocTypes = documentTypes.filter(type => type.required);
  if (!requiredDocTypes.length) {
    isStep3Validated.value = true; // No required documents for this portal type
    return true; 
  }
  
  const requiredDocIds = requiredDocTypes.map(type => type.id);
  
  if (!formData.value.documents || !Array.isArray(formData.value.documents)) {
    const tempErrors = { documents: 'Please upload all required documents' };
    if (updateErrors) errors.value = { ...errors.value, ...tempErrors };
    isStep3Validated.value = false;
    return false;
  }
  
  const hasAllRequiredDocs = requiredDocIds.every(docId => 
    formData.value.documents.some(doc => doc && doc.type === docId)
  );
  
  const tempErrors = {
    documents: !hasAllRequiredDocs ? 'Please upload all required documents' : ''
  };
  
  const isValid = !Object.values(tempErrors).some(error => error);
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  isStep3Validated.value = isValid;
  return isValid;
};

const validateStep4 = (updateErrors = true) => {
  const tempErrors = {
    effectiveDate: validateEffectiveDate(formData.value.effectiveDate),
    signature: !formData.value.signature ? 'Signature is required' : '',
    signatureDate: !formData.value.signatureDate ? 'Signature date is required' : ''
  };
  
  // Additional validation for lender type
  if (props.portalType === 'lender') {
    tempErrors.authorizationFile = !formData.value.authorizationFile ? 'Borrower authorization upload is required for lenders' : '';
  }
  
  const isValid = !Object.values(tempErrors).some(error => error);
  
  if (updateErrors) {
    errors.value = { ...errors.value, ...tempErrors };
  }
  isStep4Validated.value = isValid;
  return isValid;
};

// Watchers to update validation state when relevant data changes
watch(() => formData.value.policyNumber, () => validateStep1(false));
watch(() => formData.value.policyholderName, () => validateStep1(false));
watch(() => formData.value.email, () => validateStep1(false));

watch(() => formData.value.activeChanges, () => validateStep2(false), { deep: true });
// Add a watcher for when policyChangesRef becomes available if its validation needs to be re-triggered
watch(policyChangesRef, (newRef) => {
  if (newRef && currentStep.value === 2) {
    validateStep2(false);
  }
});

watch(() => formData.value.documents, () => validateStep3(false), { deep: true });

watch(() => formData.value.effectiveDate, () => validateStep4(false));
watch(() => formData.value.signature, () => validateStep4(false));
watch(() => formData.value.signatureDate, () => validateStep4(false));
watch(() => formData.value.authorizationFile, () => {
  if (props.portalType === 'lender') {
    validateStep4(false);
  }
});

// Navigation methods
const handleStepChange = (newStep) => {
  console.log(`[AutoPolicyChangeForm] handleStepChange called by PortalForm. New step index from PortalForm: ${newStep}`);
  currentStep.value = newStep;
};

const goToNextStep = () => {
  console.log(`[AutoPolicyChangeForm] goToNextStep called from currentStep: ${currentStep.value}`);
  let isValid = false;
  let nextStepTarget = currentStep.value + 1;

  if (currentStep.value === 1) {
    isValid = validateStep1();
  } else if (currentStep.value === 2) {
    isValid = validateStep2();
    if (props.portalType === 'lender') {
      // If lender and on step 2, next step is effectively original step 4 (Review and Submit)
      // PortalForm will handle the display index, but our logic needs to know we're targeting step 4 validation if we were to jump
      // However, PortalForm drives the step change based on the 'steps' array. We just validate current.
    }
  } else if (currentStep.value === 3 && props.portalType !== 'lender') { // Document step for customer
    isValid = validateStep3();
  }
  console.log(`[AutoPolicyChangeForm] goToNextStep: Validation result for current step (${currentStep.value}): ${isValid}`);
  // Step 4 (Review and Submit) validation is handled by its own submit button via handleSubmit

  if (isValid) {
    console.log(`[AutoPolicyChangeForm] goToNextStep: Validation PASSED for step ${currentStep.value}. Incrementing step.`);
    // Directly increment the current step if validation passes and it's not the last step
    // The last step's progression is typically handled by a submit action, not "next".
    if (currentStep.value < steps.value.length) {
        currentStep.value++;
    }
  }
};

const goToPreviousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

// Form data handling
const updateFormData = (newFormData) => {
  formData.value = newFormData;
};

const handleDraftRestored = (draft) => {
  // Any additional processing needed when a draft is restored
  // Example: Restore references or other non-reactive state
};

// Form submission
const handleSubmit = async () => {
  if (!validateStep4()) return;
  await submitForm();
};

// Helper method to prepare form data for backend API
const prepareFormDataForSubmission = () => {
  const hasDocuments = formData.value.documents && formData.value.documents.length > 0;
  const hasAuthorizationFile = props.portalType === 'lender' && formData.value.authorizationFile instanceof File;
  const isFileSubmission = hasDocuments || hasAuthorizationFile;

  let submissionPayload;
  let submissionIsMultipart;

  const baseData = {
    policyNumber: formData.value.policyNumber || '',
    policyholderName: formData.value.policyholderName || '',
    email: formData.value.email || '',
    customerType: props.portalType,
    effectiveDate: formData.value.effectiveDate || '',
    comments: formData.value.comments || '',
    signature: formData.value.signature || '',
    signatureDate: formData.value.signatureDate || '',
    addVehicleCheck: false,
    deleteVehicleCheck: false,
    replaceVehicleCheck: false,
    addDriverCheck: false,
    deleteDriverCheck: false,
    updateAddressCheck: false,
    updateCoveragesCheck: false,
    updateLienholderCheck: false,
    otherCheck: false,
  };

  if (formData.value.activeChanges && formData.value.activeChanges.length > 0) {
    formData.value.activeChanges.forEach(change => {
      switch(change.type) {
        case 'addVehicle':
          baseData.addVehicleCheck = true;
          if (change.details) {
            baseData.vehicleYear = change.details.year || '';
            baseData.vehicleMake = change.details.make || '';
            baseData.vehicleModel = change.details.model || '';
            baseData.vehicleVin = change.details.vin || '';
          }
          break;
        case 'removeVehicle':
          baseData.deleteVehicleCheck = true;
          if (change.details) {
            baseData.removeVehicleDescription = change.details.description || '';
            baseData.removeVehicleVin = change.details.vin || '';
          }
          break;
        case 'replaceVehicle':
          baseData.replaceVehicleCheck = true;
          if (change.details) {
            baseData.oldVehicleVin = change.details.oldVin || '';
            baseData.newVehicleYear = change.details.newYear || '';
            baseData.newVehicleMake = change.details.newMake || '';
            baseData.newVehicleModel = change.details.newModel || '';
            baseData.newVehicleVin = change.details.newVin || '';
          }
          break;
        case 'addDriver':
          baseData.addDriverCheck = true;
          if (change.details) {
            baseData.driverFirstName = change.details.firstName || '';
            baseData.driverLastName = change.details.lastName || '';
            baseData.driverDateOfBirth = change.details.dateOfBirth || '';
            baseData.driverLicenseNumber = change.details.licenseNumber || '';
            baseData.driverLicenseState = change.details.licenseState || '';
          }
          break;
        case 'removeDriver':
          baseData.deleteDriverCheck = true;
          if (change.details) {
            baseData.removeDriverName = change.details.name || '';
            baseData.removeDriverLicenseNumber = change.details.licenseNumber || '';
          }
          break;
        case 'updateAddress':
          baseData.updateAddressCheck = true;
          if (change.details) {
            baseData.newAddress = change.details.address || '';
            baseData.newCity = change.details.city || '';
            baseData.newState = change.details.state || '';
            baseData.newZip = change.details.zip || '';
          }
          break;
        case 'updateCoverages':
          baseData.updateCoveragesCheck = true;
          if (change.details) {
            baseData.coverageDetails = change.details.description || '';
          }
          break;
        case 'updateLienholder':
          baseData.updateLienholderCheck = true;
          if (change.details) {
            baseData.lienholderName = change.details.name || '';
            baseData.lienholderLoanNumber = change.details.loanNumber || '';
            baseData.lienholderAddress = change.details.address || '';
          }
          break;
        case 'other':
          baseData.otherCheck = true;
          if (change.details) {
            baseData.otherDetails = change.details.description || '';
          }
          break;
      }
    });
  }

  if (isFileSubmission) {
    submissionPayload = new FormData();
    submissionIsMultipart = true;
    for (const key in baseData) {
      if (Object.prototype.hasOwnProperty.call(baseData, key)) {
        submissionPayload.append(key, String(baseData[key]));
      }
    }
    if (formData.value.documents && formData.value.documents.length > 0) {
      formData.value.documents.forEach((doc, index) => {
        if (doc.file) {
          submissionPayload.append(`document_${index}`, doc.file);
          submissionPayload.append(`document_${index}_type`, doc.type || 'other');
        }
      });
    }
    if (props.portalType === 'lender' && formData.value.authorizationFile instanceof File) {
      submissionPayload.append('authorizationFile', formData.value.authorizationFile);
    }
  } else {
    submissionPayload = { ...baseData };
    submissionIsMultipart = false;
  }

  const metadata = {
    _browser: navigator.userAgent,
    _timestamp: new Date().toISOString(),
  };

  if (submissionIsMultipart) {
    submissionPayload.append('_browser', metadata._browser);
    submissionPayload.append('_timestamp', metadata._timestamp);
  } else {
    submissionPayload._browser = metadata._browser;
    submissionPayload._timestamp = metadata._timestamp;
  }
  
  return { payload: submissionPayload, isMultipart: submissionIsMultipart };
};

const submitForm = async () => {
  isSubmitting.value = true;
  errors.value.submission = null;

  if (!validateStep4(true)) {
    isSubmitting.value = false;
    return false;
  }

  loading.value = true;
  // Ensure destructuring is correct and matches the returned object from prepareFormDataForSubmission
  const { payload: dataToSubmit, isMultipart: isDataMultipart } = prepareFormDataForSubmission();

  // Enhanced logging for debugging
  console.log('[AutoPolicyChangeForm] Submitting form data:', isDataMultipart ? 
    'FormData object (see network tab for details)' : 
    JSON.stringify(dataToSubmit, null, 2));

  try {
    const response = await autoInsuranceService.submitPolicyChange(dataToSubmit, isDataMultipart);
    console.log('[AutoPolicyChangeForm] Submission successful:', response);
    
    // Set success message based on response
    if (response.message && response.message.includes('Database record creation failed')) {
      // Handle partial success case
      successMessage.value = 'Your request has been submitted to our team, but there was an issue with our database. Our team has been notified and will follow up if needed.';
      console.log('[AutoPolicyChangeForm] Partial success detected, updated message');
    }
    
    // Always advance to success step
    currentStep.value = steps.value.length + 1;
    
    // Extract reference number from response (check all possible places)
    referenceNumber.value = 
      response.referenceNumber || // First check the top-level property we added
      response.ticket_id || 
      response.policy_change_id || 
      (response.data?.ticket_id) ||
      (response.data?.policy_change_id) ||
      (response.data?.id) || 
      'N/A';
    
    if (referenceNumber.value === 'N/A' && response.message?.includes('Zendesk')) {
      // If there's a Zendesk reference in the message, try to extract it
      const match = response.message.match(/ticket #(\d+)/i);
      if (match && match[1]) {
        referenceNumber.value = match[1];
      }
    }
    
    console.log(`[AutoPolicyChangeForm] Set reference number to: ${referenceNumber.value}`);
    console.log('[AutoPolicyChangeForm] Full response:', response);
    
    successDetails.value = [
      { label: 'Reference Number', value: referenceNumber.value },
      { label: 'Policy Number', value: formData.value.policyNumber },
      { label: 'Policyholder', value: formData.value.policyholderName },
      { label: 'Effective Date', value: formData.value.effectiveDate },
    ];
    
    loading.value = false;
    isSubmitting.value = false;
    return true;
  } catch (error) {
    console.error('Error submitting form:', error);
    
    // Enhanced error logging
    if (error.originalError?.response?.data) {
      console.error('Server validation errors:', error.originalError.response.data);
    }
    
    errors.value.submission = resolveSubmissionError(error);
    logFormError('auto-policy-change', error, dataToSubmit); 
    loading.value = false;
    isSubmitting.value = false;
    return false; 
  }
};

// Helper to resolve a generic submission error message
const resolveSubmissionError = (error) => {
  if (error && error.originalError && error.originalError.response) {
    const response = error.originalError.response;
    
    // Log full error details for debugging
    console.error('[AutoPolicyChangeForm] Error details:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
    
    if (response.status === 422 && response.data.errors) {
      // Format validation errors for display
      const errorMessages = Object.entries(response.data.errors)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
        .join('; ');
      return `Validation failed: ${errorMessages}`;
    }
    
    if (response.data && response.data.message) {
      // Special handling for partial success messages (when Zendesk works but DB fails)
      if (response.data.message.includes('Database record creation failed') && 
          response.data.message.includes('submitted successfully')) {
        // This is a partial success, not a full error
        console.log('[AutoPolicyChangeForm] Detected partial success pattern in error response');
        
        // Schedule success step update
        setTimeout(() => {
          // Move to success step
          currentStep.value = steps.value.length + 1;
          successMessage.value = 'Your request has been submitted to our team, but there was an issue with our database. Our team has been notified and will follow up if needed.';
        }, 100);
        
        return ''; // Empty error to not show error UI
      }
      
      return `Submission failed: ${response.data.message}`;
    }
  }
  
  if (error && error.message) {
    return `An error occurred: ${error.message}`;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

// Handle submission actions
const handleEmailSent = (email) => {
  console.log(`Confirmation email sent to ${email}`);
};

const returnToDashboard = () => {
  router.push('/dashboard');
};

const navigateAway = () => {
  router.push('/forms');
};
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.input-responsive {
  @apply mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm;
}

.form-field {
  @apply mb-4;
}
</style> 